/**
 * Dashboard Executivo DP / Folha — Centro de comando das 13 fases
 * /dp-folha/dashboard
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Users, FileText, FileCode, AlertTriangle, Mail, Home, Building2,
  Loader2, RefreshCw, Play, Calendar, CheckCircle2, XCircle, ArrowRight,
  Banknote, Bell, CalendarClock, CalendarHeart, Cake,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const Brl = ({ v }) => <>R$ {Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</>;

const SEVERIDADE_BADGE = {
  urgente: 'bg-red-100 text-red-700 border-red-300',
  atencao: 'bg-amber-100 text-amber-700 border-amber-300',
  info: 'bg-blue-100 text-blue-700 border-blue-300',
};

const ALERTA_SEV_BADGE = {
  alta: 'bg-red-100 text-red-700 border-red-300',
  media: 'bg-amber-100 text-amber-700 border-amber-300',
  baixa: 'bg-blue-100 text-blue-700 border-blue-300',
};

const CATEGORIA_ICON = {
  ferias_em_atraso: CalendarClock,
  ferias_proximas: CalendarHeart,
  experiencia_termina: CalendarClock,
  fim_contrato: CalendarClock,
  aniversario: Cake,
  aso_vencido: AlertTriangle,
  aso_proximo: Bell,
  prazo_esocial: FileCode,
  prazo_reinf: FileCode,
};

const ModuleCard = ({ icon: Icon, title, link, color, stats }) => {
  const navigate = useNavigate();
  return (
    <Card className="cursor-pointer hover:shadow-md transition" onClick={() => link && navigate(link)}
          data-testid={`dashboard-card-${title.toLowerCase().replace(/[^a-z]/g, '')}`}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className={`h-5 w-5 ${color}`} />{title}
        </CardTitle>
        {link && <ArrowRight className="h-4 w-4 text-slate-400" />}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 gap-2 text-sm">
          {stats.map(s => (
            <div key={s.k}>
              <div className="text-xs text-slate-500">{s.k}</div>
              <div className={`text-xl font-bold ${s.danger ? 'text-red-600' : s.warn ? 'text-amber-600' : 'text-slate-800'}`}>{s.v}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default function DpDashboardPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');     // '' = todas
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/companies`, auth()).then(r => setCompanies(r.data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = companyId ? { company_id: companyId } : {};
      const r = await axios.get(`${API}/api/dp-folha/dashboard`, { ...auth(), params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar dashboard');
    } finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const rodarJob = async (jobName, label) => {
    setRunning(jobName);
    try {
      const r = await axios.post(`${API}/api/dp-folha/scheduler/run/${jobName}`, {}, auth());
      const j = r.data;
      if (jobName === 'det_polling_auto') {
        toast.success(`DET: ${j.empresas} empresas verificadas, ${j.novas_mensagens} nova(s) mensagem(ns)`);
      } else if (jobName === 'reinf_fechamento_mensal') {
        toast.success(`Reinf ${j.per_apur}: ${j.total_criados} fechamento(s) criado(s), ${j.pulados?.length || 0} pulado(s)`);
      } else {
        toast.success(`${label} executado`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao executar job');
    } finally { setRunning(null); }
  };

  if (loading && !data) return (
    <div className="p-6 flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Carregando dashboard...</div>
  );
  if (!data) return null;

  const totalPendencias = data.pendencias?.length || 0;
  const urgentes = data.pendencias?.filter(p => p.severidade === 'urgente').length || 0;

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="dp-dashboard-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-7 w-7 text-indigo-600" />Dashboard DP / Folha
            <Badge variant="outline" className="ml-2">Centro de Comando</Badge>
          </h1>
          <p className="text-sm text-slate-500">
            Visão consolidada — Competência atual: <b>{data.competencia_atual}</b> · Anterior: <b>{data.competencia_anterior}</b>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={companyId || 'todas'} onValueChange={v => setCompanyId(v === 'todas' ? '' : v)}>
            <SelectTrigger className="w-64" data-testid="dashboard-company-select"><Building2 className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as empresas</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.razao_social}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="dashboard-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alerta de pendências */}
      {totalPendencias > 0 && (
        <Card className={urgentes > 0 ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${urgentes > 0 ? 'text-red-600' : 'text-amber-600'}`} />
              {urgentes > 0 ? `${urgentes} pendência(s) URGENTE(s) · ${totalPendencias} no total` : `${totalPendencias} pendência(s) requerem atenção`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {data.pendencias.map((p, i) => (
                <button key={i}
                        onClick={() => navigate(p.link)}
                        className="w-full text-left flex items-center justify-between px-3 py-2 bg-white rounded border hover:bg-slate-50"
                        data-testid={`dashboard-pendencia-${i}`}>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={SEVERIDADE_BADGE[p.severidade]}>{p.modulo}</Badge>
                    <span className="text-sm"><b className="text-base">{p.qtd}</b> · {p.msg}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agenda do DP — alertas preditivos */}
      {data.alertas && data.alertas.kpis.total > 0 && (
        <Card className="border-indigo-200" data-testid="dashboard-agenda-dp">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-5 w-5 text-indigo-600" />Agenda do DP — Alertas Preditivos
              </CardTitle>
              <CardDescription>
                {data.alertas.kpis.total} alertas · {data.alertas.kpis.por_severidade.alta} alta(s) · {data.alertas.kpis.por_severidade.media} média(s) · {data.alertas.kpis.por_severidade.baixa} baixa(s)
              </CardDescription>
            </div>
            <Button size="sm" variant="ghost" onClick={() => navigate('/dp-folha/alertas')}
                    data-testid="dashboard-ver-todos-alertas">Ver todos<ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.alertas.top.map((a, i) => {
                const Icon = CATEGORIA_ICON[a.category] || Bell;
                return (
                  <button key={i}
                          onClick={() => navigate(a.link || '/dp-folha/funcionarios')}
                          className="text-left border rounded-lg p-3 hover:bg-slate-50 bg-white flex items-start gap-3"
                          data-testid={`dashboard-alerta-${i}`}>
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${a.severity === 'alta' ? 'text-red-600' : a.severity === 'media' ? 'text-amber-600' : 'text-blue-600'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm">{a.title}</div>
                        <Badge variant="outline" className={`${ALERTA_SEV_BADGE[a.severity]} text-xs`}>{a.severity}</Badge>
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5 truncate">{a.description}</div>
                      {a.due_date && (
                        <div className="text-xs text-slate-400 mt-1">Prazo: {a.due_date}{a.days_remaining !== null && ` · ${a.days_remaining < 0 ? Math.abs(a.days_remaining) + ' dia(s) em atraso' : a.days_remaining + ' dia(s)'}`}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid de módulos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <ModuleCard icon={Users} title="Folha" color="text-blue-600" link="/dp-folha/funcionarios"
          stats={[
            { k: 'Funcionários Ativos', v: data.folha.funcionarios_ativos },
            { k: 'Rescisões', v: data.folha.rescisoes },
            { k: 'Folhas processadas', v: data.folha.folhas_processadas },
          ]} />
        <ModuleCard icon={FileCode} title="eSocial" color="text-indigo-600" link="/dp-folha/esocial"
          stats={[
            { k: 'Total', v: data.esocial.total },
            { k: 'Pendentes', v: data.esocial.pendentes, warn: data.esocial.pendentes > 0 },
            { k: 'Rejeitados', v: data.esocial.rejeitados, danger: data.esocial.rejeitados > 0 },
          ]} />
        <ModuleCard icon={FileCode} title="EFD-Reinf" color="text-purple-600" link="/dp-folha/reinf"
          stats={[
            { k: 'Total', v: data.reinf.total },
            { k: 'Pendentes', v: data.reinf.pendentes, warn: data.reinf.pendentes > 0 },
            { k: 'R-2099 anterior', v: data.reinf.r2099_fechado ? '✓ OK' : '⚠ Aberto', warn: !data.reinf.r2099_fechado },
            { k: 'R-4099 anterior', v: data.reinf.r4099_fechado ? '✓ OK' : '⚠ Aberto', warn: !data.reinf.r4099_fechado },
          ]} />
        <ModuleCard icon={Banknote} title="FGTS Digital" color="text-emerald-600" link="/dp-folha/fgts"
          stats={[
            { k: 'Total Guias', v: data.fgts.total },
            { k: 'A Pagar', v: <Brl v={data.fgts.valor_total_recolher} /> },
            { k: 'Rascunhos', v: data.fgts.rascunhos, warn: data.fgts.rascunhos > 0 },
            { k: 'Transmitidas', v: data.fgts.a_pagar },
          ]} />
        <ModuleCard icon={Mail} title="DET (MTE)" color="text-amber-600" link="/dp-folha/det"
          stats={[
            { k: 'Não lidas', v: data.det.nao_lidas, warn: data.det.nao_lidas > 0 },
            { k: 'Urgentes', v: data.det.urgentes, danger: data.det.urgentes > 0 },
          ]} />
        <ModuleCard icon={Home} title="eSocial Doméstico" color="text-pink-600" link="/dp-folha/esocial-domestico"
          stats={[
            { k: 'Empregadores', v: data.domestico.empregadores },
            { k: 'Empregados', v: data.domestico.empregados },
            { k: 'Eventos Pendentes', v: data.domestico.eventos_pendentes, warn: data.domestico.eventos_pendentes > 0 },
          ]} />
      </div>

      {/* Scheduler & jobs automáticos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-indigo-600" />Automações</CardTitle>
          <CardDescription>Jobs em segundo plano + execução manual</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-600" />Polling DET (MTE)
                </div>
                <Badge variant="outline" className="text-xs">A cada 1h · :15</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">Verifica caixa postal MTE de todas as empresas e envia email para mensagens novas.</p>
              <Button size="sm" variant="outline" className="mt-2" disabled={running === 'det_polling_auto'}
                      onClick={() => rodarJob('det_polling_auto', 'DET Polling')}
                      data-testid="dashboard-run-det-job">
                {running === 'det_polling_auto' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}Executar agora
              </Button>
            </div>
            <div className="border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm flex items-center gap-2">
                  <FileCode className="h-4 w-4 text-purple-600" />Fechamento Reinf mensal
                </div>
                <Badge variant="outline" className="text-xs">Dia 1 · 02:00 BR</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">Gera R-2099 e R-4099 da competência anterior automaticamente (idempotente).</p>
              <Button size="sm" variant="outline" className="mt-2" disabled={running === 'reinf_fechamento_mensal'}
                      onClick={() => rodarJob('reinf_fechamento_mensal', 'Fechamento Reinf')}
                      data-testid="dashboard-run-reinf-job">
                {running === 'reinf_fechamento_mensal' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}Executar agora
              </Button>
            </div>
          </div>

          {data.ultimos_jobs_scheduler?.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs font-medium text-slate-600 mb-2">Últimas execuções</div>
              <div className="space-y-1">
                {data.ultimos_jobs_scheduler.map((j, i) => (
                  <div key={i} className="text-xs flex items-center justify-between border-b last:border-0 pb-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                      <span className="font-mono">{j.job}</span>
                    </div>
                    <div className="text-slate-500">
                      {(j.executed_at || '').slice(0, 16).replace('T', ' ')}
                      {j.total_criados !== undefined && ` · ${j.total_criados} criados`}
                      {j.novas_mensagens !== undefined && ` · ${j.novas_mensagens} novas`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
